import type { PopulatedOrgMember, TaskStatus } from "@api/types";
import { getCustomerName } from "@lib/utils";
import {
  AlarmClock,
  CalendarPlus,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
} from "lucide-react";
import Link from "next/link";
import { type ReactNode, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CustomerAvatar } from "@/modules/customer/customer-avatar";
import { AssigneePicker } from "./assignee-picker";
import { DueDatePicker } from "./due-date-picker";
import {
  descriptionSnippet,
  dueDateLabel,
  getUserName,
  isTaskOverdue,
  type PopulatedTask,
  TASK_STATUS_LABELS,
  TaskUserAvatar,
} from "./shared";
import { SnoozeControl } from "./snooze";
import { TASK_STATUS_DOTS, TaskStatusBadge } from "./status-badge";

export type TaskUpdateBody = Record<string, unknown>;

interface TasksListProps {
  tasks?: PopulatedTask[];
  isLoading?: boolean;
  members?: PopulatedOrgMember[];
  selectedId?: string;
  emptyState?: ReactNode;
  /** Drop the contact column when every task belongs to the same contact. */
  hideCustomer?: boolean;
  onSelect: (task: PopulatedTask) => void;
  onUpdate: (task: PopulatedTask, body: TaskUpdateBody) => void;
}

/** Section order for the status-grouped list. */
const STATUS_ORDER: TaskStatus[] = ["todo", "in_progress", "snoozed", "completed"];

/**
 * Within-section ordering: open tasks by due date (undated last, newest
 * first), snoozed by wake date, completed by completion recency.
 */
function compareWithinStatus(status: TaskStatus, a: PopulatedTask, b: PopulatedTask): number {
  if (status === "completed") {
    return (b.completedAt ?? b.updatedAt ?? "").localeCompare(a.completedAt ?? a.updatedAt ?? "");
  }
  if (status === "snoozed") {
    return (a.snoozedUntil ?? "").localeCompare(b.snoozedUntil ?? "");
  }
  if (!a.dueDate && !b.dueDate) return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
  if (!a.dueDate) return 1;
  if (!b.dueDate) return -1;
  return a.dueDate.localeCompare(b.dueDate);
}

/** Inline status control: badge that opens a quick status/snooze menu. */
function StatusMenu({
  task,
  onUpdate,
}: {
  task: PopulatedTask;
  onUpdate: (task: PopulatedTask, body: TaskUpdateBody) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Change status"
          className="flex max-w-full rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(e) => e.stopPropagation()}
        >
          <TaskStatusBadge
            status={task.status}
            snoozedUntil={task.snoozedUntil}
            className="cursor-pointer transition-colors hover:bg-muted/60"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48" onClick={(e) => e.stopPropagation()}>
        {(["todo", "in_progress", "completed"] as TaskStatus[]).map((status) => (
          <DropdownMenuItem
            key={status}
            className="gap-2"
            onSelect={() => {
              if (status !== task.status) onUpdate(task, { status });
            }}
          >
            <div className={cn("size-1.5 rounded-full", TASK_STATUS_DOTS[status])} />
            {TASK_STATUS_LABELS[status]}
          </DropdownMenuItem>
        ))}
        {task.status === "snoozed" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2" onSelect={() => onUpdate(task, { status: "todo" })}>
              <AlarmClock className="size-3.5 text-muted-foreground" />
              Unsnooze
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TaskRow({
  task,
  members,
  selected,
  hideCustomer,
  onSelect,
  onUpdate,
}: {
  task: PopulatedTask;
  members?: PopulatedOrgMember[];
  selected: boolean;
  hideCustomer?: boolean;
  onSelect: (task: PopulatedTask) => void;
  onUpdate: (task: PopulatedTask, body: TaskUpdateBody) => void;
}) {
  const completed = task.status === "completed";
  const overdue = isTaskOverdue(task);
  const customer = hideCustomer || typeof task.customer !== "object" ? undefined : task.customer;

  return (
    <li>
      {/* Same container-query recipe as the call log rows: the available
          width depends on the app sidebar / detail drawer, so columns key
          off the list container. Narrow: two-line layout; @3xl: single-line
          columns. Rendered as a div (not a button) because the row hosts
          real interactive controls — pickers, menus — for inline editing. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect(task)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target === e.currentTarget) onSelect(task);
        }}
        aria-current={selected || undefined}
        className={cn(
          "group flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors @3xl:py-2 md:px-6",
          selected ? "bg-muted/70" : "hover:bg-muted/50",
        )}
      >
        <button
          type="button"
          aria-label={completed ? "Reopen task" : "Complete task"}
          className="peer shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate(task, { status: completed ? "todo" : "completed" });
          }}
        >
          {completed ? (
            <CheckCircle2 className="size-[18px] text-green-500 dark:text-green-400" />
          ) : (
            <Circle className="size-[18px] text-muted-foreground/40 transition-colors hover:text-green-500 dark:hover:text-green-400" />
          )}
        </button>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span
              className={cn(
                "truncate text-sm font-medium text-foreground transition-colors peer-hover:text-muted-foreground",
                completed && "text-muted-foreground line-through",
              )}
            >
              {task.title}
            </span>
            {task.tags.slice(0, 2).map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="hidden shrink-0 px-1.5 py-0 text-[11px] font-normal @5xl:inline-flex"
              >
                {tag}
              </Badge>
            ))}
          </span>
          {/* Narrow layout: meta collapses under the title */}
          <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground @3xl:hidden">
            {customer ? <span className="truncate">{getCustomerName(customer)}</span> : null}
            {customer && task.dueDate ? <span aria-hidden="true">·</span> : null}
            {task.dueDate ? (
              <span className={cn("whitespace-nowrap", overdue && "text-destructive")}>
                {dueDateLabel(task.dueDate)}
              </span>
            ) : null}
          </span>
          {task.description ? (
            <span className="mt-0.5 hidden truncate text-xs text-muted-foreground/80 @3xl:block">
              {descriptionSnippet(task.description)}
            </span>
          ) : null}
        </span>

        {/* Inline-edit columns reveal progressively as the list widens so
            the title always keeps readable room (drawer may take half).
            Scheduling cluster stays together: status badge, due date,
            snooze — all describing "when". */}
        <span
          className="hidden w-56 min-w-0 shrink-0 @3xl:block"
          onClick={(e) => e.stopPropagation()}
        >
          <StatusMenu task={task} onUpdate={onUpdate} />
        </span>

        <span className="hidden w-32 shrink-0 @3xl:block" onClick={(e) => e.stopPropagation()}>
          <DueDatePicker
            value={task.dueDate ? new Date(task.dueDate) : null}
            onChange={(date) => onUpdate(task, { dueDate: date })}
            align="end"
            trigger={
              <button
                type="button"
                aria-label="Change due date"
                className="flex max-w-full items-center gap-1 rounded-md px-1.5 py-1 -mx-1.5 -my-1 transition-colors hover:bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {task.dueDate ? (
                  <span
                    className={cn(
                      "truncate whitespace-nowrap text-xs",
                      overdue ? "text-destructive" : "text-muted-foreground",
                    )}
                  >
                    {dueDateLabel(task.dueDate)}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground/60">
                    <CalendarPlus className="size-3.5" aria-hidden="true" />
                    Add due date
                  </span>
                )}
              </button>
            }
          />
        </span>

        {/* First-class snooze on every open task; placeholder keeps the
            columns aligned on completed rows. */}
        <span className="hidden w-6 shrink-0 @3xl:block" onClick={(e) => e.stopPropagation()}>
          {!completed && (
            <SnoozeControl
              snoozedUntil={task.status === "snoozed" ? task.snoozedUntil : undefined}
              onSnooze={(until) => onUpdate(task, { status: "snoozed", snoozedUntil: until })}
              onUnsnooze={() => onUpdate(task, { status: "todo" })}
              tooltip={task.status === "snoozed" ? "Change snooze" : "Snooze"}
              trigger={
                <button
                  type="button"
                  aria-label="Snooze task"
                  className={cn(
                    "flex items-center justify-center rounded-md p-1 transition-colors hover:bg-muted hover:text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    task.status === "snoozed"
                      ? "text-muted-foreground"
                      : "text-muted-foreground/50",
                  )}
                >
                  <AlarmClock className="size-4" />
                </button>
              }
            />
          )}
        </span>

        {/* Contact pill: same treatment as the thread/task headers. */}
        {!hideCustomer && (
          <span className="hidden w-44 shrink-0 @5xl:block">
            {customer ? (
              <Link
                className="flex h-7 min-w-0 max-w-full items-center rounded-full border border-transparent px-1 text-sm font-medium transition-colors hover:border-border hover:bg-secondary hover:shadow-sm"
                href={`/contacts/${customer.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <CustomerAvatar customer={customer} className="mr-1.5 size-5 shrink-0 text-[9px]" />
                <span className="truncate text-xs">{getCustomerName(customer)}</span>
                <ChevronRight className="ml-1 size-3 shrink-0 text-muted-foreground" />
              </Link>
            ) : null}
          </span>
        )}

        {/* Assignee: same control as the detail panel. The column stays
            fixed for alignment but the button hugs its content so there's
            no dead space after the name. */}
        <span
          className="hidden w-40 shrink-0 justify-end @4xl:flex"
          onClick={(e) => e.stopPropagation()}
        >
          <AssigneePicker
            value={task.user?.id ?? null}
            members={members}
            onChange={(userId) => onUpdate(task, { user: userId })}
            align="end"
          >
            <Button
              variant="outline"
              size="sm"
              className="h-8 max-w-full justify-start gap-2 font-normal shadow-none"
              aria-label="Change assignee"
            >
              {task.user ? (
                <>
                  <TaskUserAvatar user={task.user} />
                  <span className="truncate text-xs">{getUserName(task.user)}</span>
                </>
              ) : (
                <>
                  <TaskUserAvatar user={null} className="opacity-50" />
                  <span className="truncate text-xs text-muted-foreground">Assign to...</span>
                </>
              )}
            </Button>
          </AssigneePicker>
        </span>
      </div>
    </li>
  );
}

function StatusSection({
  status,
  tasks,
  members,
  selectedId,
  hideCustomer,
  onSelect,
  onUpdate,
}: {
  status: TaskStatus;
  tasks: PopulatedTask[];
  members?: PopulatedOrgMember[];
  selectedId?: string;
  hideCustomer?: boolean;
  onSelect: (task: PopulatedTask) => void;
  onUpdate: (task: PopulatedTask, body: TaskUpdateBody) => void;
}) {
  // Completed stays out of the way until asked for; open sections start
  // expanded so the whole active queue is visible by default.
  const [open, setOpen] = useState(status !== "completed");
  const overdueCount =
    status === "completed" ? 0 : tasks.filter((task) => isTaskOverdue(task)).length;

  return (
    <section>
      <h2 className="sticky top-0 z-[1] border-y border-subtle bg-background/95 backdrop-blur first:border-t-0">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="flex w-full items-center gap-1.5 px-4 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:text-foreground md:px-6"
        >
          <ChevronDown
            className={cn("size-3.5 shrink-0 transition-transform", !open && "-rotate-90")}
            aria-hidden="true"
          />
          <span className={cn("size-1.5 rounded-full", TASK_STATUS_DOTS[status])} />
          {TASK_STATUS_LABELS[status]}
          <span className="text-muted-foreground/60">{tasks.length}</span>
          {overdueCount > 0 && <span className="text-destructive">· {overdueCount} overdue</span>}
        </button>
      </h2>
      {open && (
        <ul className="divide-y divide-border/60">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              members={members}
              selected={task.id === selectedId}
              hideCustomer={hideCustomer}
              onSelect={onSelect}
              onUpdate={onUpdate}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function TasksList(props: TasksListProps) {
  const sections = useMemo(() => {
    const groups = new Map<TaskStatus, PopulatedTask[]>();
    for (const task of props.tasks ?? []) {
      const list = groups.get(task.status);
      if (list) list.push(task);
      else groups.set(task.status, [task]);
    }
    return STATUS_ORDER.filter((status) => groups.has(status)).map((status) => ({
      status,
      tasks: groups.get(status)!.toSorted((a, b) => compareWithinStatus(status, a, b)),
    }));
  }, [props.tasks]);

  if (props.isLoading && !props.tasks) {
    return (
      <div className="space-y-3 px-4 py-4 md:px-6">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-[18px] rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-56" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
    );
  }

  if (!props.tasks?.length) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-4 py-16 text-center">
        {props.emptyState}
      </div>
    );
  }

  return (
    <>
      {sections.map((section) => (
        <StatusSection
          key={section.status}
          status={section.status}
          tasks={section.tasks}
          members={props.members}
          selectedId={props.selectedId}
          hideCustomer={props.hideCustomer}
          onSelect={props.onSelect}
          onUpdate={props.onUpdate}
        />
      ))}
    </>
  );
}
